import { Card, Flex } from "antd"
import React from "react"
import { type BlockerFunction } from "react-router-dom"

import ReactRouterPrompt from "../../../../../../components/ReactRouterPrompt.tsx"
import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { WorkspaceUnsavedChangesModal } from "../../../../../Lead/LeadWorkspacePage/cards/WorkspaceUnsavedChangesModal.tsx"
import {
  WorkspaceEditGuardProvider,
  useWorkspaceEditGuard
} from "../../../../../Lead/LeadWorkspacePage/cards/workspace-edit-guard.tsx"
import {
  type AssignedManager,
  type ManagerField,
  ProjectManagersFields
} from "../../../../ProjectEditPage/overview/ProjectManagersFields.tsx"
import { ProjectOverviewCards } from "../../../../ProjectEditPage/overview/ProjectOverviewCards.tsx"
import { canEditProject } from "../../../../helpers/project-edit-visibility.ts"
import { canViewFinanceTab } from "../../../../helpers/project-finance-visibility.ts"
import {
  fetchFinancialAlerts,
  fetchFinancialSnapshot
} from "../../../../redux/project-finance/project-finance.thunks.ts"
import {
  addProjectTypeToProject,
  removeProjectTypeToProject,
  updateProject
} from "../../../../redux/project.thunks.ts"
import { type ProjectUpdateData } from "../../../../types/project.types.ts"

// The header's Contract Value and Contract Expenses read the financial
// snapshot, not the project record, so a save of either figure reloads it.
const CONTRACT_FIGURES = ["sales_price", "planned_payable"] as const

function ProjectViewOverviewBody() {
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.project.project)
  const filterOptions = useAppSelector((state) => state.projects.filterOptions)
  const projectPermissions = useAppSelector(
    (state) => state.auth.permissions?.codes?.projects
  )
  // Same source as the Edit Details page, so both surfaces lock the same way.
  const assignPMRight = useAppSelector(
    (state) =>
      !!state.auth?.userInfo?.permissions?.codes?.projects?.features
        ?.project_assign_pm
  )
  const assignSalesRight = useAppSelector(
    (state) =>
      !!state.auth?.userInfo?.permissions?.codes?.leads?.features
        ?.lead_assign_sales_manager
  )
  const { hasUnsavedChanges, discardAll } = useWorkspaceEditGuard()

  if (!project) return null

  const canEdit = canEditProject(projectPermissions, project)

  const handleCardPatch = async (patch: Partial<ProjectUpdateData>) => {
    try {
      await dispatch(
        updateProject({ id: project.id, ...patch } as ProjectUpdateData)
      ).unwrap()
      if (CONTRACT_FIGURES.some((key) => key in patch)) {
        void dispatch(fetchFinancialSnapshot(project.id))
        void dispatch(fetchFinancialAlerts(project.id))
      }
      return true
    } catch (e) {
      console.error("Update project error:", e)
      return false
    }
  }

  const handleManagersChange = async (
    field: ManagerField,
    next: AssignedManager[]
  ) => {
    try {
      await dispatch(
        updateProject({ id: project.id, [field]: next } as ProjectUpdateData)
      ).unwrap()
    } catch (e) {
      console.error("Update project error:", e)
    }
  }

  const blockNavigation: BlockerFunction = (args) => {
    if (!hasUnsavedChanges) return false
    if (!args?.nextLocation) return true
    return args.currentLocation.pathname !== args.nextLocation.pathname
  }

  return (
    <Flex vertical gap={"small"} className={"min-h-fit"}>
      <Card size={"small"} styles={{ body: { padding: "12px 20px" } }}>
        <ProjectManagersFields
          project={project}
          filterOptions={filterOptions}
          assignedPMAllowed={canEdit && assignPMRight}
          assignedSalesAllowed={canEdit && assignSalesRight}
          onChange={handleManagersChange}
        />
      </Card>

      <ProjectOverviewCards
        project={project}
        filterOptions={filterOptions}
        projectPermissions={projectPermissions}
        canEdit={canEdit}
        showFinancialSummary={canViewFinanceTab(projectPermissions)}
        showFiles={!!projectPermissions?.docs?.project_doc_view}
        onSave={handleCardPatch}
        addProjectType={(args) => dispatch(addProjectTypeToProject(args))}
        removeProjectType={(args) => dispatch(removeProjectTypeToProject(args))}
      />

      <ReactRouterPrompt when={blockNavigation}>
        {({ isActive, onConfirm, onCancel }) => (
          <WorkspaceUnsavedChangesModal
            open={isActive}
            onStay={onCancel}
            onDiscard={() => {
              discardAll()
              onConfirm()
            }}
          />
        )}
      </ReactRouterPrompt>
    </Flex>
  )
}

export default function ProjectViewOverviewSection() {
  return (
    <WorkspaceEditGuardProvider>
      <ProjectViewOverviewBody />
    </WorkspaceEditGuardProvider>
  )
}
